import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CREATION_TYPES, TOPIC_CATEGORIES, TOPIC_ITEMS, FONT_FAMILIES, LETTER_CONSTRUCTIONS } from '@/data/optionsCore';
import { PATTERN_FAMILIES, MATERIAL_FAMILIES, FINISHES, SUPPORTING_ELEMENTS, COMPOSITION_LAYOUTS } from '@/data/optionsLibrary';
import { PRESETS } from '@/data/presets';
import { FONT_DATABASE } from '@/data/fontDatabase';
import { useStudio } from '@/store/StudioStore';

function buildIndex() {
  const items = [];
  CREATION_TYPES.forEach(c => items.push({ kind: 'Creation Type', label: c.label, action: { creationType: c.id, creationTypeLabel: c.label } }));
  TOPIC_CATEGORIES.forEach(c => items.push({ kind: 'Topic Category', label: c.label, action: { topicCategory: c.id, topicCategoryLabel: c.label } }));
  Object.entries(TOPIC_ITEMS).forEach(([cat, list]) => list.forEach(t => items.push({ kind: 'Topic', label: t, action: { topicItem: t } })));
  FONT_FAMILIES.forEach(f => f.styles.forEach(s => items.push({ kind: 'Font Direction', label: `${s} · ${f.label}`, action: { fontFamily: f.id, fontFamilyLabel: f.label, fontStyle: s } })));
  FONT_DATABASE.forEach(f => items.push({ kind: 'Font', label: f.name + ' · ' + f.classification, action: { namedFont: f.name } }));
  LETTER_CONSTRUCTIONS.forEach(l => items.push({ kind: 'Construction', label: l, action: { letterConstruction: l } }));
  Object.entries(PATTERN_FAMILIES).forEach(([fam, list]) => list.forEach(p => items.push({ kind: 'Pattern', label: `${p} · ${fam}`, action: { patternFamily: fam, patternSubtype: p } })));
  Object.entries(MATERIAL_FAMILIES).forEach(([fam, list]) => list.forEach(m => items.push({ kind: 'Material', label: `${m} · ${fam}`, action: { materialFamily: fam, materialSubtype: m } })));
  FINISHES.forEach(f => items.push({ kind: 'Finish', label: f, action: { finish: f } }));
  Object.entries(SUPPORTING_ELEMENTS).forEach(([grp, list]) => list.forEach(s => items.push({ kind: 'Element', label: `${s} · ${grp}`, action: { _pushSupporting: s } })));
  COMPOSITION_LAYOUTS.forEach(c => items.push({ kind: 'Composition', label: c, action: { composition: c } }));
  PRESETS.forEach(p => items.push({ kind: 'Preset', label: p.name, action: { _preset: p } }));
  return items;
}

const IDX = buildIndex();

export default function SmartSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const { dispatch } = useStudio();
  const nav = useNavigate();

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const needle = q.toLowerCase();
    return IDX.filter(i => i.label.toLowerCase().includes(needle) || i.kind.toLowerCase().includes(needle)).slice(0, 40);
  }, [q]);

  const apply = (item) => {
    if (item.action._preset) {
      dispatch({ type: 'PATCH', patch: item.action._preset.recipe });
    } else if (item.action._pushSupporting) {
      dispatch({ type: 'TOGGLE_ARR', key: 'supporting', value: item.action._pushSupporting });
    } else {
      dispatch({ type: 'PATCH', patch: item.action });
    }
    setOpen(false); setQ('');
    nav('/studio');
  };

  return (
    <div className="relative" data-testid="smart-search">
      <input
        data-testid="smart-search-input"
        value={q}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search fonts, materials, holidays, presets…"
        className="bg-zinc-950 border border-zinc-800 rounded-full px-4 py-1.5 text-xs w-56 lg:w-72 focus:border-pink-500 outline-none"
      />
      {open && results.length > 0 && (
        <div className="absolute right-0 mt-2 w-[420px] max-h-96 overflow-y-auto scrollbar-thin rounded-xl chrome-border bg-zinc-950/95 backdrop-blur p-2 z-50" data-testid="smart-search-results">
          {results.map((r, i) => (
            <button key={i} onMouseDown={() => apply(r)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-pink-500/10 flex items-center justify-between text-xs">
              <span className="text-zinc-200">{r.label}</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{r.kind}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
